/* eslint-disable camelcase */
import React from 'react';
import ReactTable from 'react-table';
import ActionButton from '../../common/ActionButton';

export type TAgencyTimesheetSummary = {
    agency_id: number;
    agency_name: string;
    awaiting_approval_count: number;
    auto_approved_count: number;
    approved_count: number;
    total_count: number;
    downloaded_count: number;
};

type TPayrollAdminTableProps = {
    content: TAgencyTimesheetSummary[];
    loading: boolean;
    errorMsg?: string;
    onDownload: (agencyId: number, onlyNew?: boolean) => void;
    onAutoApprove: (agencyId: number) => void;
    canAutoApprove: boolean;
    autoApprovingAgencyId?: number;
};

type TableCell<TData> = {
    original: TData;
};

export default function PayrollAdminTable({
    content,
    loading,
    errorMsg,
    onDownload,
    onAutoApprove,
    canAutoApprove,
    autoApprovingAgencyId,
}: TPayrollAdminTableProps) {
    const autoApproveColumn = {
        Header: 'Auto-approve timesheets',
        sortable: false,
        Cell: (row: TableCell<TAgencyTimesheetSummary>) => {
            const isAutoApproving =
                autoApprovingAgencyId === row.original.agency_id;
            const hasAwaitingApproval =
                row.original.awaiting_approval_count > 0;
            const isDisabledByAwaitingApproval = !hasAwaitingApproval;

            return (
                <ActionButton
                    text={isAutoApproving ? 'Auto-approving...' : 'Auto-approve'}
                    compact={true}
                    className="payroll-admin-auto-approve-btn"
                    disabled={isAutoApproving || isDisabledByAwaitingApproval}
                    tooltip={
                        isDisabledByAwaitingApproval
                            ? 'No timesheets awaiting approval.'
                            : undefined
                    }
                    onAction={() => onAutoApprove(row.original.agency_id)}
                />
            );
        },
    };

    const columns = [
        {
            Header: 'Agency',
            accessor: 'agency_name',
            minWidth: 200,
        },
        {
            Header: 'Awaiting Approval',
            accessor: 'awaiting_approval_count',
            minWidth: 140,
            maxWidth: 160,
        },
        {
            Header: 'Auto Approved',
            accessor: 'auto_approved_count',
            minWidth: 120,
            maxWidth: 140,
        },
        {
            Header: 'Approved',
            accessor: 'approved_count',
            minWidth: 90,
            maxWidth: 110,
        },
        {
            Header: 'Total',
            accessor: 'total_count',
            minWidth: 80,
            maxWidth: 100,
        },
        {
            Header: 'Downloaded',
            id: 'downloaded_count',
            minWidth: 100,
            maxWidth: 120,
            accessor: (row: TAgencyTimesheetSummary) =>
                `${row.downloaded_count}/${row.total_count}`,
            Cell: (row: TableCell<TAgencyTimesheetSummary>) => {
                const { downloaded_count, total_count } = row.original;
                const isComplete = downloaded_count === total_count;
                const backgroundColor = isComplete ? '#d4edda' : '#fff3cd';
                const color = isComplete ? '#155724' : '#856404';
                return (
                    <span
                        style={{
                            backgroundColor,
                            color,
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontWeight: 600,
                        }}
                    >
                        {downloaded_count}/{total_count}
                    </span>
                );
            },
        },
        ...(canAutoApprove ? [autoApproveColumn] : []),
        {
            Header: 'Download New CSV',
            sortable: false,
            Cell: (row: TableCell<TAgencyTimesheetSummary>) => {
                const hasNoNewTimesheets =
                    row.original.downloaded_count === row.original.total_count;

                return (
                    <ActionButton
                        text="Download New CSV"
                        compact={true}
                        disabled={hasNoNewTimesheets}
                        tooltip={
                            hasNoNewTimesheets
                                ? 'No new timesheets available to download.'
                                : undefined
                        }
                        onAction={() => onDownload(row.original.agency_id, true)}
                    />
                );
            },
        },
        {
            Header: 'Download All CSV',
            sortable: false,
            Cell: (row: TableCell<TAgencyTimesheetSummary>) => (
                <ActionButton
                    text="Download CSV"
                    compact={true}
                    onAction={() => onDownload(row.original.agency_id)}
                />
            ),
        },
    ];

    return (
        <ReactTable
            className="table-content payroll-admin-table"
            loading={loading}
            data={content || []}
            columns={columns}
            pageSize={content ? content.length : 0}
            noDataText={errorMsg || 'No timesheets Found'}
            showPaginationBottom={false}
            showPageSizeOptions={false}
        />
    );
}
